import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Disclosure, Transition } from "@headlessui/react";
import { CheckIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { LightningBoltIcon } from "@heroicons/react/outline";
import { CodeIcon } from "lucide-react";
import { InboxActionData, InboxActionLog } from "@hilos/types/inbox_action";
import { InboxContactRead } from "@hilos/types/private-schema";
import LoaderSpinner from "../../../../components/LoaderSpinner";
import { getRenderedMessage } from "../../../../helpers/template";
import useCleanTimeout from "../../../../hooks/useCleanTimeout";
import { useCustomFields } from "../../../../hooks/useContactCustomField";
import useHilosStore from "../../../../hooks/useHilosStore";
import useInboxActionDetails from "../../../../hooks/useInboxActionDetails";
import { useInboxActionList } from "../../../../hooks/useInboxActionList";
import InboxActionLogModal from "./InboxActionLogModal";

interface InboxActionListProps {
  inboxContact: InboxContactRead;
}

function InboxActionList({ inboxContact }: InboxActionListProps) {
  const { t } = useTranslation();
  const { inbox_actions } = useInboxActionList();
  const { executeAction } = useInboxActionDetails();
  const timeout = useCleanTimeout();
  const { session } = useHilosStore();
  const { contactCustomFields } = useCustomFields();

  const [executing, setExecuting] = useState(false);
  const [executingAction, setExecutingAction] =
    useState<InboxActionData | null>(null);
  const [executionSuccess, setExecutionSuccess] = useState(false);
  const [lastExecutionResult, setLastExecutionResult] =
    useState<InboxActionLog | null>(null);
  const [open, setOpen] = useState(false);

  const processVariables = useCallback(
    (text) => {
      if (!text || !session) return text;

      const processed = getRenderedMessage(
        text,
        inboxContact.contact,
        session,
        contactCustomFields
      );
      return processed;
    },
    [session, inboxContact.contact, contactCustomFields]
  );

  const processJsonBody = useCallback(
    (obj) => {
      if (typeof obj !== "object" || obj === null) {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map((item) => processJsonBody(item));
      }

      const result = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          if (typeof value === "string") {
            result[key] = processVariables(value);
          } else if (typeof value === "object" && value !== null) {
            result[key] = processJsonBody(value);
          } else {
            result[key] = value;
          }
        }
      }
      return result;
    },
    [processVariables]
  );

  const processHttpAction = useCallback(
    (action) => {
      if (action.action_type !== "HTTP") return action;

      const processedAction = JSON.parse(JSON.stringify(action));

      if (processedAction.request_url) {
        processedAction.request_url = processVariables(
          processedAction.request_url
        );
      }

      if (
        processedAction.request_body &&
        typeof processedAction.request_body === "string"
      ) {
        try {
          const contentType =
            processedAction.request_headers?.["Content-Type"] ||
            processedAction.request_headers?.["content-type"];

          if (contentType && contentType.includes("application/json")) {
            try {
              const bodyObj = JSON.parse(processedAction.request_body);
              const processedBody = processJsonBody(bodyObj);
              processedAction.request_body = JSON.stringify(processedBody);
            } catch (err) {
              processedAction.request_body = processVariables(
                processedAction.request_body
              );
            }
          } else {
            processedAction.request_body = processVariables(
              processedAction.request_body
            );
          }
        } catch (err) {
          processedAction.request_body = processVariables(
            processedAction.request_body
          );
        }
      }

      if (
        processedAction.request_headers &&
        typeof processedAction.request_headers === "object"
      ) {
        Object.keys(processedAction.request_headers).forEach((key) => {
          const value = processedAction.request_headers[key];
          if (typeof value === "string") {
            processedAction.request_headers[key] = processVariables(value);
          }
        });
      }

      if (
        processedAction.request_params &&
        typeof processedAction.request_params === "object"
      ) {
        Object.keys(processedAction.request_params).forEach((key) => {
          const value = processedAction.request_params[key];
          if (typeof value === "string") {
            processedAction.request_params[key] = processVariables(value);
          }
        });
      }

      return processedAction;
    },
    [processJsonBody, processVariables]
  );

  const handleExecuteAction = async (action: InboxActionData) => {
    setExecuting(true);
    setExecutingAction(action);
    setLastExecutionResult(null);
    try {
      let result;

      if (action.action_type === "HTTP") {
        const processedAction = processHttpAction(action);

        result = await executeAction(
          inboxContact.contact.id as string,
          action,
          {
            variablesProcessed: true,
            requestUrl: processedAction.request_url,
            requestBody: processedAction.request_body,
            requestHeaders: processedAction.request_headers,
            requestParams: processedAction.request_params,
          }
        );
      } else {
        result = await executeAction(inboxContact.contact.id as string, action);
      }

      setExecutionSuccess(result.success);
      setLastExecutionResult(result);
      if (!result.success) {
        setOpen(true);
      }

      timeout.current = setTimeout(() => {
        setExecutingAction(null);
        setExecutionSuccess(false);
      }, 1000 * 2);
    } catch (err) {
      setExecutionSuccess(false);
      timeout.current = setTimeout(() => {
        setExecutingAction(null);
      }, 1000 * 2);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      <div className="px-3">
        <Disclosure defaultOpen={true}>
          {({ open }) => (
            <>
              <Disclosure.Button className="flex min-w-full flex-1 grow items-center justify-between text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t("integrations", "Integrations")}
                <ChevronRightIcon
                  className={`mr-1.5 h-4 w-4 text-gray-500 ${
                    open
                      ? "rotate-90 transform transition-transform duration-100 ease-in-out"
                      : ""
                  }`}
                />
              </Disclosure.Button>
              <Transition
                show={open}
                enter="transition duration-100 ease-in"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Disclosure.Panel className="py-3 text-sm text-gray-500">
                  <div>
                    <ul className="space-y-2">
                      {inbox_actions.results.map((action) => (
                        <li key={action.id} id={action.id}>
                          <button
                            type="button"
                            className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onClick={(_) => handleExecuteAction(action)}
                            disabled={executing}
                          >
                            {executingAction === action ? (
                              <>
                                {executionSuccess ? (
                                  <div className="inline-flex w-full items-center justify-center text-green-500">
                                    <CheckIcon className="text-green mr-1 h-5 w-5" />
                                    {t("success", "Success")}!
                                  </div>
                                ) : (
                                  <>
                                    <LoaderSpinner />
                                    {t("executing", "Executing...")}
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {action.action_type === "HTTP" ? (
                                  <CodeIcon className="mr-2 h-5 w-5" />
                                ) : (
                                  <LightningBoltIcon className="mr-2 h-5 w-5" />
                                )}
                                {action.button_label}
                              </>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Disclosure.Panel>
              </Transition>
            </>
          )}
        </Disclosure>
      </div>
      {lastExecutionResult && (
        <InboxActionLogModal
          inboxActionLog={lastExecutionResult}
          open={open}
          setOpen={setOpen}
        />
      )}
    </>
  );
}
export default InboxActionList;
